import React, { useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Grid, Select, MenuItem, OutlinedInput, InputAdornment, IconButton, FormHelperText, FormControl
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import useFormularioInscricao from './useFormularioInscricao';
import ContainedButton from '../../../shared/buttons/ContainedButton';
import TextButton from '../../../shared/buttons/TextButton';
import Close from '@material-ui/icons/CloseOutlined';
import Save from '@material-ui/icons/SaveOutlined';
import * as Yup from 'yup';
import { CepNumberFormatCustom } from '../../../shared/formatters';
import Search from '@material-ui/icons/SearchOutlined';
import LabelValue from "../../../shared/labelvalue/LabelValue.js";
import useFetch from "../../../../hooks/useFetch.js";
import { URL_ENDERECO_CEPS } from "../../../../constants";
import DialogBuscarEndereco from './DialogBuscarEndereco.jsx';
import InstructionMessage from '../../../shared/instructionmessage/InstructionMessage.js';

const useStyles = makeStyles(theme => ({
    dialogContent: {
        paddingTop: theme.spacing(2)
    }
}));

const validationSchema = Yup.object().shape({
    tipoEndereco: Yup.string().required('Campo obrigatório'),
    cep: Yup.string().required('Campo obrigatório')
        .test('completo', 'Preencha o campo completo', valor => !valor || !String(valor).includes('_')),
    logradouro: Yup.string().required('Campo obrigatório'),
    numero: Yup.string().required('Campo obrigatório'),
    bairro: Yup.string().required('Campo obrigatório'),
    cidade: Yup.mixed().nullable().required('Campo obrigatório')
});

export default function DialogEnderecoComposicaoFamiliar({ open, onClose, onSave, initialValues, tipos = [], enderecos = [] }) {
    const classes = useStyles();
    const [fetch] = useFetch({ returnInnerLoadingState: true });
    const [buscarEnderecoOpen, setBuscarEnderecoOpen] = React.useState(false);

    const hasResidencial = (enderecos || []).some(
        (end) => (end.tipoEndereco === 'RESIDENCIAL' || end.tipoEndereco?.nome === 'RESIDENCIAL') &&
                 end !== initialValues &&
                 (!initialValues || end.id == null || end.id !== initialValues.id)
    );

    const formDialogo = useFormularioInscricao({
        valoresIniciais: {
            tipoEndereco: '',
            cep: '',
            logradouro: '',
            numero: '',
            complemento: '',
            bairro: '',
            cidade: null,
            uf: ''
        },
        schema: validationSchema,
        aoSubmeter: (values) => {
            onSave(values);
        }
    });

    useEffect(() => {
        if (open) {
            if (initialValues) {
                let initialTipo = initialValues.tipoEndereco;
                if (initialTipo && typeof initialTipo === 'object') {
                    initialTipo = initialTipo.nome || '';
                }
                formDialogo.reiniciar({
                    values: {
                        ...initialValues,
                        tipoEndereco: initialTipo || '',
                        uf: initialValues.uf || initialValues.cidade?.uf?.uf || '',
                        complemento: initialValues.complemento || '',
                        cidade: initialValues.cidade || null
                    }
                });
            } else {
                formDialogo.reiniciar({
                    values: { tipoEndereco: '', cep: '', logradouro: '', numero: '', complemento: '', bairro: '', cidade: null, uf: '' }
                });
            }
        }
    }, [open, initialValues]);

    const handleCepBlur = async (e) => {
        formDialogo.aoSair(e);
        const cep = e.target.value.replace(/\D/g, '');
        if (cep.length === 8) {
            fetch(`${URL_ENDERECO_CEPS}/${cep}`,
                null,
                (data) => {
                    if (data) {
                        formDialogo.definirValor('logradouro', data.logradouro || '');
                        formDialogo.definirValor('bairro', data.bairro || '');
                        formDialogo.definirValor('cidade', data.cidade || null);
                        formDialogo.definirValor('uf', data.cidade?.uf?.uf || '');
                    }
                },
                (error, tratarErro) => {
                    tratarErro(error);
                    formDialogo.definirValor('logradouro', '');
                    formDialogo.definirValor('bairro', '');
                    formDialogo.definirValor('cidade', null);
                    formDialogo.definirValor('uf', '');
                }
            );
        }
    };

    return (
        <>
            <Dialog scroll="body" open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle>{initialValues && initialValues.cep ? 'Editar Endereço' : 'Adicionar Endereço'}</DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <InstructionMessage
                                title="Orientações sobre Tipo de Endereço"
                                type="info"
                                variant="standard"
                                collapsible={true}
                                defaultExpanded={true}
                                items={[
                                    "Residencial - Moradia atual do candidato/aluno",
                                    "Domiciliar - Moradia de origem do grupo familiar",
                                    "Comercial - Endereço adicional"
                                ]}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <LabelValue label="Tipo do Endereço *">
                                <FormControl error={formDialogo.tocados.tipoEndereco && !!formDialogo.erros.tipoEndereco} fullWidth variant="outlined">
                                    <Select
                                        name="tipoEndereco"
                                        value={formDialogo.valores.tipoEndereco}
                                        onChange={formDialogo.aoMudar}
                                        onBlur={formDialogo.aoSair}
                                    >
                                        <MenuItem value="">Selecione...</MenuItem>
                                        {tipos && tipos.length > 0 ? (
                                            tipos.map((t) => {
                                                const isResidencial = t.nome === 'RESIDENCIAL';
                                                const isDisabled = isResidencial && hasResidencial;
                                                return (
                                                    <MenuItem key={t.nome} value={t.nome} disabled={isDisabled}>
                                                        {t.descricao} {isDisabled ? '(Já cadastrado)' : ''}
                                                    </MenuItem>
                                                );
                                            })
                                        ) : (
                                            <>
                                                <MenuItem value="RESIDENCIAL" disabled={hasResidencial}>
                                                    Residencial {hasResidencial ? '(Já cadastrado)' : ''}
                                                </MenuItem>
                                                <MenuItem value="DOMICILIAR">Domiciliar</MenuItem>
                                                <MenuItem value="COMERCIAL">Comercial</MenuItem>
                                            </>
                                        )}
                                    </Select>
                                    {formDialogo.tocados.tipoEndereco && formDialogo.erros.tipoEndereco && (
                                        <FormHelperText>{formDialogo.erros.tipoEndereco}</FormHelperText>
                                    )}
                                </FormControl>
                            </LabelValue>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <LabelValue label="CEP *">
                                <FormControl variant="outlined" fullWidth error={formDialogo.tocados.cep && !!formDialogo.erros.cep}>
                                    <OutlinedInput
                                        name="cep"
                                        inputComponent={CepNumberFormatCustom}
                                        value={typeof formDialogo.valores.cep === 'number'
                                            ? String(formDialogo.valores.cep).padStart(8, '0') // do banco: sem os zeros à esquerda
                                            : String(formDialogo.valores.cep ?? '')}
                                        onChange={formDialogo.aoMudar}
                                        onBlur={handleCepBlur}
                                        placeholder="00000-000"
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setBuscarEnderecoOpen(true)} edge="end">
                                                    <Search />
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                    />
                                    {formDialogo.tocados.cep && formDialogo.erros.cep && (
                                        <FormHelperText>{formDialogo.erros.cep}</FormHelperText>
                                    )}
                                </FormControl>
                            </LabelValue>
                        </Grid>
                        <Grid item xs={12} sm={9}>
                            <LabelValue label="Logradouro *">
                                <FormControl variant="outlined" fullWidth error={formDialogo.tocados.logradouro && !!formDialogo.erros.logradouro}>
                                    <OutlinedInput
                                        name="logradouro"
                                        value={formDialogo.valores.logradouro}
                                        onChange={formDialogo.aoMudar}
                                        onBlur={formDialogo.aoSair}
                                    />
                                    {formDialogo.tocados.logradouro && formDialogo.erros.logradouro && (
                                        <FormHelperText>{formDialogo.erros.logradouro}</FormHelperText>
                                    )}
                                </FormControl>
                            </LabelValue>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <LabelValue label="Número *">
                                <FormControl variant="outlined" fullWidth error={formDialogo.tocados.numero && !!formDialogo.erros.numero}>
                                    <OutlinedInput
                                        name="numero"
                                        value={formDialogo.valores.numero}
                                        onChange={formDialogo.aoMudar}
                                        onBlur={formDialogo.aoSair}
                                    />
                                    {formDialogo.tocados.numero && formDialogo.erros.numero && (
                                        <FormHelperText>{formDialogo.erros.numero}</FormHelperText>
                                    )}
                                </FormControl>
                            </LabelValue>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <LabelValue label="Bairro *">
                                <FormControl variant="outlined" fullWidth error={formDialogo.tocados.bairro && !!formDialogo.erros.bairro}>
                                    <OutlinedInput
                                        name="bairro"
                                        value={formDialogo.valores.bairro}
                                        onChange={formDialogo.aoMudar}
                                        onBlur={formDialogo.aoSair}
                                    />
                                    {formDialogo.tocados.bairro && formDialogo.erros.bairro && (
                                        <FormHelperText>{formDialogo.erros.bairro}</FormHelperText>
                                    )}
                                </FormControl>
                            </LabelValue>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <LabelValue label="Complemento">
                                <OutlinedInput
                                    name="complemento"
                                    fullWidth
                                    value={formDialogo.valores.complemento}
                                    onChange={formDialogo.aoMudar}
                                    onBlur={formDialogo.aoSair}
                                />
                            </LabelValue>
                        </Grid>
                        <Grid item xs={12}>
                            <LabelValue label="Cidade *">
                                <FormControl variant="outlined" fullWidth error={formDialogo.tocados.cidade && !!formDialogo.erros.cidade}>
                                    <OutlinedInput
                                        fullWidth
                                        disabled
                                        value={formDialogo.valores.cidade ? `${formDialogo.valores.cidade.nome || formDialogo.valores.cidade} - ${formDialogo.valores.uf || ''}` : 'Pesquise pelo CEP para obter a cidade'}
                                    />
                                    {formDialogo.tocados.cidade && formDialogo.erros.cidade && (
                                        <FormHelperText>{formDialogo.erros.cidade}</FormHelperText>
                                    )}
                                </FormControl>
                            </LabelValue>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <TextButton onClick={onClose} startIcon={<Close />}>Fechar</TextButton>
                    <ContainedButton onClick={formDialogo.submeter} startIcon={<Save />} disabled={formDialogo.enviando}>
                        Salvar
                    </ContainedButton>
                </DialogActions>
            </Dialog>

            <DialogBuscarEndereco
                open={buscarEnderecoOpen}
                onClose={() => setBuscarEnderecoOpen(false)}
                onSelect={(selectedCepObj) => {
                    formDialogo.definirValor('cep', selectedCepObj.cep || '');
                    formDialogo.definirValor('logradouro', selectedCepObj.logradouro || '');
                    formDialogo.definirValor('bairro', selectedCepObj.bairro || '');
                    formDialogo.definirValor('cidade', selectedCepObj.cidade || null);
                    formDialogo.definirValor('uf', selectedCepObj.cidade?.uf?.uf || '');
                    setBuscarEnderecoOpen(false);
                }}
            />
        </>
    );
}
